import type { AxiosInstance } from 'axios';
import { unwrap, unwrapItems, type ObjectResponse } from '../core/envelope';
import type { TenantLanguage } from '../data/printFormat';

/**
 * Languages the tenant publishes content in.
 *
 * Adding a language is a data change rather than a deploy — the SPA previously
 * hardcoded a two-entry list whose switcher had no effect at all.
 *
 * `remove` is rejected server-side for the default language: documents fall back
 * to it when a string has no translation, so deleting it would leave every
 * partially translated format quietly printing built-in English.
 */
export function createTenantLanguageService(client: AxiosInstance) {
  return {
    /** Every language, including inactive ones — for the admin grid. */
    all: async (): Promise<TenantLanguage[]> => {
      const res = await client.post<ObjectResponse<TenantLanguage[]>>('/TenantLanguage/all', {});
      return unwrapItems(res.data);
    },

    /** Active languages in display order — what pickers should bind to. */
    active: async (): Promise<TenantLanguage[]> => {
      const res = await client.post<ObjectResponse<TenantLanguage[]>>('/TenantLanguage/active', {});
      return unwrapItems(res.data);
    },

    create: async (items: TenantLanguage[]): Promise<boolean> => {
      const res = await client.post<ObjectResponse<boolean>>('/TenantLanguage', { items });
      return unwrap(res.data) ?? false;
    },

    update: async (items: TenantLanguage[]): Promise<boolean> => {
      const res = await client.put<ObjectResponse<boolean>>('/TenantLanguage', { items });
      return unwrap(res.data) ?? false;
    },

    remove: async (language: TenantLanguage): Promise<boolean> => {
      const res = await client.post<ObjectResponse<boolean>>('/TenantLanguage/Delete', language);
      return unwrap(res.data) ?? false;
    },
  };
}

export type TenantLanguageService = ReturnType<typeof createTenantLanguageService>;
